// components/FileEditorModal/FileEditorModal.tsx
import React, { useRef, useEffect, useState } from "react";
import moment from "moment";
import Spreadsheet from "x-data-spreadsheet";
import "x-data-spreadsheet/dist/xspreadsheet.css";
import Button from "components/Button";
import { FileEditorConfig } from "interfaces/fileEditor";
import { fileToSheetData, spreadsheetToFile } from "utils/fileEditorHelpers";
import styles from "../styles/modules/FileEditorModal.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { State } from "state";
import { uploadFile, FileUploadState } from "state/create_file_upload";
import { Dispatch } from "interfaces/dispatch";
import { convertSpreadsheetToFile } from "utils/spreadsheetDataConverter";

if (typeof window !== "undefined") {
  const resizeObserverErrHandler = () => {
    // Ignore the ResizeObserver loop error
  };
  window.addEventListener("error", (e) => {
    if (e.message === "ResizeObserver loop completed with undelivered notifications.") {
      e.stopImmediatePropagation();
    }
  });
  window.addEventListener("unhandledrejection", resizeObserverErrHandler);
}

interface FileEditorModalProps {
  callCallbackFunction?: boolean;
  callOuterFunction?: (editedFile: File) => void;
  isOpen: boolean;
  file: File | null;
  error?: string | null;
  config: FileEditorConfig;
  onClose: () => void;
  prefix: string;
}

export const FileEditorModal: React.FC<FileEditorModalProps> = ({
  callCallbackFunction,
  callOuterFunction,
  isOpen,
  file,
  error,
  config,
  onClose,
  prefix,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const spreadsheetRef = useRef<any>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [currentError, setCurrentError] = useState<string | null>(null);
  const dispatch = useDispatch<Dispatch>();

  const uploadState = useSelector((state: State) => {
    // @ts-ignore
    return state[`${prefix}_file_upload`];
  });

  useEffect(() => {
    // Clear error when modal opens or when user starts a new attempt
    setCurrentError(null);
  }, [isOpen]);

  // Inject global CSS styles to fix text overlap
  useEffect(() => {
    const styleId = "file-editor-fix-styles";
    
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        /* Prevent text overflow in all cells */
        .x-spreadsheet-cell-text {
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          white-space: nowrap !important;
        }
        
        /* Hide background text when editing */
        .x-spreadsheet-table td.selected .x-spreadsheet-cell,
        .x-spreadsheet-table td.selected .x-spreadsheet-cell-text {
          visibility: hidden !important;
        }
        
        /* Editor styling with white background */
        .x-spreadsheet-editor {
          z-index: 100 !important;
        }
        
        .x-spreadsheet-editor .x-spreadsheet-editor-area {
          background: white !important;
          border: 2px solid #4A90E2 !important;
          box-sizing: border-box !important;
        }
        
        .x-spreadsheet-editor .x-spreadsheet-editor-area textarea {
          background: white !important;
          color: #000 !important;
          padding: 2px 6px !important;
          box-sizing: border-box !important;
          font-family: Arial, sans-serif !important;
          font-size: 13px !important;
        }
      `;
      document.head.appendChild(style);
    }

    return () => {
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, []);

  // Initialize spreadsheet when modal opens
  useEffect(() => {
    if (isOpen && file && containerRef.current) {
      console.log("Initializing spreadsheet");
      initializeSpreadsheet();
      setCurrentError(error || null);
    }

    return () => {
      if (spreadsheetRef.current) {
        try {
          spreadsheetRef.current.destroy?.();
        } catch (e) {
          console.warn("Error destroying spreadsheet:", e);
        }
        spreadsheetRef.current = null;
      }
    };
  }, [isOpen, file, error]);

  useEffect(() => {
    if (isUploading && uploadState) {
      const { loading, hasError, errors } = uploadState;


      if (loading) {
        return;
      }

      // ✅ API Response received - Success case
      if (!loading && !hasError) {
        setCurrentError(null);
        setIsUploading(false);

        if (config.onUploadSuccess) {
          config.onUploadSuccess();
        }
      }

      if (!loading && hasError) {
        console.error("Upload error:", errors);
        const errorMessage = errors && errors.length > 0 ? errors[0] : "Upload failed";
        setCurrentError(errorMessage);
        setIsUploading(false);
      }
    }
  }, [uploadState?.loading, uploadState?.hasError, uploadState?.errors, isUploading]);

  // Helper function to calculate optimal column width based on content
  const calculateColumnWidth = (text: string): number => {
    const charWidth = 8;
    const padding = 20;
    const minWidth = 100;
    const maxWidth = 400;
    
    const calculatedWidth = (text?.length || 0) * charWidth + padding;
    return Math.min(Math.max(calculatedWidth, minWidth), maxWidth);
  };

  const initializeSpreadsheet = async () => {
    try {
      if (!file || !containerRef.current) return;

      const sheetData: any = await fileToSheetData(file);

      // Calculate optimal column widths based on content
      const columnWidths: { [key: number]: number } = {};
      const rows = sheetData.rows || {};
      
      // Analyze all cells to find the maximum content width per column
      Object.values(rows).forEach((row: any) => {
        const cells = row.cells || {};
        Object.entries(cells).forEach(([colIndex, cell]: [string, any]) => {
          const width = calculateColumnWidth(cell.text || '');
          const col = parseInt(colIndex);
          columnWidths[col] = Math.max(columnWidths[col] || 0, width);
        });
      });

      // Apply calculated widths to sheet data
      if (!sheetData.cols) {
        sheetData.cols = {};
      }
      
      Object.entries(columnWidths).forEach(([col, width]) => {
        sheetData.cols[parseInt(col)] = { width };
      });

      // Set default width for any columns without content
      const maxCol = Math.max(
        ...Object.keys(columnWidths).map(Number),
        10 // Ensure at least 10 columns
      );
      
      for (let i = 0; i <= maxCol; i++) {
        if (!sheetData.cols[i]) {
          sheetData.cols[i] = { width: 150 };
        }
      }

      if (containerRef.current) {
        containerRef.current.innerHTML = "";
        spreadsheetRef.current = new Spreadsheet(containerRef.current, {
          showToolbar: true,
          showGrid: true,
          col: {
            len: maxCol + 1,
            width: 150,
            indexWidth: 60,
            minWidth: 60,
          },
        }).loadData([sheetData]);

        // Force re-render after a short delay to ensure proper sizing
        setTimeout(() => {
          if (spreadsheetRef.current) {
            try {
              spreadsheetRef.current.reRender();
            } catch (e) {
              console.warn("Error during initial re-render:", e);
            }
          }
        }, 100);
      }
    } catch (err) {
      console.error("Error loading file into spreadsheet:", err);
    }
  };

  useEffect(() => {
    if (spreadsheetRef.current && isOpen) {
      // Listen for cell changes and force re-render
      const handleCellChange = () => {
        // Small delay to ensure the change is complete
        setTimeout(() => {
          if (spreadsheetRef.current) {
            try {
              spreadsheetRef.current.reRender();
            } catch (e) {
              console.warn("Error during re-render:", e);
            }
          }
        }, 10);
      };

      // You might need to access the internal events
      // This depends on x-data-spreadsheet's event system
      const spreadsheetEl = containerRef.current;
      if (spreadsheetEl) {
        spreadsheetEl.addEventListener("input", handleCellChange);
        spreadsheetEl.addEventListener("blur", handleCellChange);

        return () => {
          spreadsheetEl.removeEventListener("input", handleCellChange);
          spreadsheetEl.removeEventListener("blur", handleCellChange);
        };
      }
    }
  }, [isOpen, spreadsheetRef.current]);

  const handleSave = async () => {
    try {
      setIsUploading(true);

      setCurrentError(null);

      if (config.beforeRetryUpload && file) {
        const shouldContinue = await config.beforeRetryUpload(file);
        if (!shouldContinue) {
          setIsUploading(false);
          return;
        }
      }

      if (!spreadsheetRef.current) {
        throw new Error("Spreadsheet not initialized");
      }

      const spreadsheetData = spreadsheetRef.current.getData();

      const fileType = file?.name.split(".").pop()?.toLowerCase() === "csv" ? "csv" : "xlsx";
      const editedFile = spreadsheetToFile(spreadsheetData, file!.name, fileType);

      if (callCallbackFunction && callOuterFunction) {
        callOuterFunction(editedFile);
        return;
      } else {
        await uploadFileViaRedux(editedFile);
      }
    } catch (err) {
      // Only catch client-side errors (not API errors)
      console.error("Error saving file:", err);
      setIsUploading(false);
      const errorMessage = err instanceof Error ? err.message : "Error saving file";
      setCurrentError(errorMessage);
    }
  };

  const uploadFileViaRedux = async (editedFile: File) => {
    const formData = new FormData();
    formData.append("file", editedFile);

    const offset = moment().utcOffset();
    const timezone = moment.tz.guess();
    formData.append("offset", `${offset}`);
    formData.append("timezone", timezone);

    if (config.additionalFormDataAppend) {
      config.additionalFormDataAppend(formData, editedFile);
    }

    dispatch(uploadFile(prefix, config.url, { offset, timezone })({ data: formData }));
  };

  if (!isOpen) {
    return null;
  }

  // console.log("Rendering modal, currentError:", currentError, "isUploading:", isUploading);
  return (
    <div
      className={styles.overlay}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="editor-title"
    >
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h3 id="editor-title">{`Edit: ${file?.name}`}</h3>
          <button
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close"
            disabled={isUploading}
          >
            ×
          </button>
        </div>

        {currentError && (
          <div className={styles.errorBanner}>
            <p>
              <strong>Upload Error:</strong> {currentError}
            </p>
            <p>Please review and correct the errors below, then save again.</p>
          </div>
        )}

        <div className={styles.spreadsheetContainer} ref={containerRef} />

        <div className={styles.footer}>
          <button
            style={{
              border: "1px solid #E5B42F",
              backgroundColor: "#ffffff",
              color: "#000",
              fontWeight: 500,
              padding: "8px 16px",
              borderRadius: "4px",
              cursor: isUploading ? "not-allowed" : "pointer",
              opacity: isUploading ? 0.6 : 1,
            }}
            onClick={onClose}
            disabled={isUploading}
          >
            Cancel
          </button>
          <button
            style={{
              border: "1px solid #d2d2d2",
              backgroundColor: "#E5B42F",
              color: "#000",
              fontWeight: 500,
              padding: "8px 16px",
              borderRadius: "4px",
              cursor: isUploading ? "not-allowed" : "pointer",
              opacity: isUploading ? 0.6 : 1,
            }}
            // onClick={callCallbackFunction ? callOuterFunction : handleSave}
            onClick={handleSave}
            disabled={isUploading}
          >
            {isUploading ? "Saving..." : "Save & Upload"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FileEditorModal;
